<template>
  <div class="app" :class="{ 'app-sidebar-minimized': isSidebarMinimized }">
    <notifications group="app" position="top center">
      <template #body="props">
        <b-alert :variant="props.item.type" show class="m-2 text-center">{{
          props.item.text
        }}</b-alert>
      </template>
    </notifications>

    <!-- Mobile Toggle Button -->
    <div class="mobile-sidebar-toggle d-lg-none">
      <button
        class="btn btn-link sidebar-toggle-btn"
        type="button"
        @click="toggleSidebar"
      >
        <i class="fa fa-bars"></i>
      </button>
    </div>

    <div class="app-body">
      <AppSidebar fixed>
        <div class="sidebar-collapse-control d-none d-lg-flex">
          <button
            class="btn btn-link sidebar-collapse-btn"
            type="button"
            :aria-label="
              isSidebarMinimized
                ? 'Expandir menu lateral'
                : 'Recolher menu lateral'
            "
            @click="toggleSidebarMinimize"
          >
            <i
              class="fa"
              :class="isSidebarMinimized ? 'fa-angle-right' : 'fa-angle-left'"
            ></i>
          </button>
        </div>

        <!-- User Profile Section in Sidebar -->
        <div
          class="sidebar-user-section"
          :class="{ 'is-compact': !showSidebarExtras }"
        >
          <template v-if="showSidebarExtras">
            <div class="user-profile-dropdown" @click="toggleUserMenu">
              <div class="user-info">
                <div class="user-greeting">Bem-vindo(a),</div>
                <div class="user-name">
                  {{ profile.name }} {{ profile.surname }}
                </div>
                <div class="user-email">{{ profile.email }}</div>
              </div>
              <div class="dropdown-arrow">
                <i
                  class="fa fa-chevron-down"
                  :class="{ rotated: showUserMenu }"
                ></i>
              </div>
            </div>

            <div v-show="showUserMenu" class="user-actions-dropdown">
              <b-button
                variant="link"
                size="sm"
                to="/perfil"
                class="user-action-btn"
              >
                <i class="fa fa-user"></i> Perfil
              </b-button>
              <b-button
                variant="link"
                size="sm"
                to="/alterar-senha"
                class="user-action-btn"
              >
                <i class="fa fa-lock"></i> Senha
              </b-button>
            </div>
          </template>

          <template v-else>
            <div class="user-compact-actions">
              <button
                type="button"
                class="user-compact-action"
                :class="{ 'is-open': showCompactUserMenu }"
                title="Conta"
                aria-label="Abrir menu da conta"
                @click="toggleCompactUserMenu"
              >
                <i class="fa fa-user"></i>
              </button>
            </div>

            <div v-show="showCompactUserMenu" class="user-compact-panel">
              <b-button
                variant="link"
                size="sm"
                to="/perfil"
                class="user-action-btn compact-panel-btn"
              >
                <i class="fa fa-user"></i> Perfil
              </b-button>
              <b-button
                variant="link"
                size="sm"
                to="/alterar-senha"
                class="user-action-btn compact-panel-btn"
              >
                <i class="fa fa-lock"></i> Senha
              </b-button>
            </div>
          </template>
        </div>

        <div v-show="showSidebarExtras" class="sidebar-top-divider"></div>

        <!-- Protocol Search Section -->
        <div
          v-show="showSidebarExtras"
          class="sidebar-search-section px-3 py-3"
        >
          <b-input-group size="sm" class="search-input-group">
            <b-form-input
              v-model="protocolSearch"
              placeholder="Pesquisar protocolo..."
              class="search-input"
              @keyup.enter="onProtocolSearch"
            />
            <b-input-group-append>
              <b-button
                variant="link"
                class="search-btn"
                @click="onProtocolSearch"
              >
                <i class="fas fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <SidebarHeader v-show="showSidebarExtras" />
        <SidebarForm v-show="showSidebarExtras" />
        <SidebarNav :nav-items="nav"></SidebarNav>
        <b-navbar-nav>
          <b-nav-item v-if="showSupportMenu" :to="{ path: '/suporte' }">
            <i class="nav-icon fa fa-life-ring" aria-hidden="true"></i>
            Suporte
            <b-badge
              v-if="menuNotificationCounts.support > 0"
              variant="secondary"
              pill
              class="ml-2"
            >
              {{ menuNotificationCounts.support }}
            </b-badge>
          </b-nav-item>
          <b-nav-item href="/manual/suporte.html"
            ><i class="nav-icon fa fa-book" aria-hidden="true"></i
            >Manual</b-nav-item
          >
        </b-navbar-nav>

        <!-- Theme Toggle Section -->
        <div
          class="sidebar-theme-section"
          :class="{ 'is-compact': !showSidebarExtras }"
        >
          <div v-if="showSidebarExtras" class="theme-toggle-wrapper">
            <div class="theme-switch" role="group" aria-label="Selecionar tema">
              <button
                type="button"
                class="theme-option-btn"
                :class="{ 'is-active': isLightTheme }"
                @click="toggleTheme('light')"
              >
                <i class="fas fa-sun theme-option-icon"></i>
                <span>Light</span>
              </button>
              <button
                type="button"
                class="theme-option-btn"
                :class="{ 'is-active': !isLightTheme }"
                @click="toggleTheme('dark')"
              >
                <i class="fas fa-moon theme-option-icon"></i>
                <span>Dark</span>
              </button>
            </div>
          </div>
          <button
            v-else
            type="button"
            class="theme-compact-btn"
            :aria-label="
              isLightTheme ? 'Ativar tema escuro' : 'Ativar tema claro'
            "
            :title="isLightTheme ? 'Alternar para dark' : 'Alternar para light'"
            @click="toggleTheme(isLightTheme ? 'dark' : 'light')"
          >
            <i
              class="theme-option-icon"
              :class="isLightTheme ? 'fas fa-sun' : 'fas fa-moon'"
            ></i>
          </button>
        </div>

        <div
          class="sidebar-logout-section"
          :class="{ 'is-compact': !showSidebarExtras }"
        >
          <b-button
            v-if="showSidebarExtras"
            variant="link"
            size="sm"
            class="user-action-btn user-logout-action logout-btn"
            type="button"
            @click="logout"
          >
            <span class="logout-btn-icon" aria-hidden="true">
              <i class="fas fa-sign-out-alt"></i>
            </span>
            <span class="logout-btn-label">Sair</span>
          </b-button>
          <button
            v-else
            type="button"
            class="user-compact-action user-compact-logout"
            title="Sair"
            aria-label="Sair"
            @click="logout"
          >
            <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
          </button>
        </div>
      </AppSidebar>
      <main class="main">
        <template v-if="$route.meta.noContainer">
          <router-view></router-view>
        </template>
        <div v-else class="container-fluid">
          <router-view></router-view>
        </div>
      </main>
    </div>
    <TheFooter>
      <!--footer-->
      <div></div>
      <div class="ml-auto">
        <a href="http://conformita.com.br/">Conformità</a>
        <span class="ml-1">&copy; 2022</span>
      </div>
    </TheFooter>
  </div>
</template>

<script>
import {
  Sidebar as AppSidebar,
  SidebarForm,
  SidebarHeader,
  SidebarNav,
  Footer as TheFooter,
} from "@coreui/vue";

import { mapGetters } from "vuex";
import axios from "axios";
import ComplaintStatus from "../../../backend/models/ComplaintStatus.ts";
import { getComplaints } from "../api/complaint";
import { getDSARNotifications } from "../api/dsar";
import { getOmbudsmanNotifications } from "../api/ombudsman";
import { getQaSNotifications } from "../api/qas";
import { getSupportNotifications } from "../api/support";
import { logout as logoutApi } from "../api/user";
import protocol from "../mixins/protocol";

const MENU_NOTIFICATION_REFRESH_INTERVAL = 60000;

const MENU_NOTIFICATION_ROUTE_MAP = {
  "/denuncias": "complaints",
  "/duvidas-e-sugestoes": "qas",
  "/lgpd": "dsar",
  "/ouvidoria": "ombudsman",
  "/suporte": "support",
};

export default {
  name: "DefaultContainer",
  components: {
    AppSidebar,
    TheFooter,
    SidebarForm,
    SidebarHeader,
    SidebarNav,
  },
  mixins: [protocol],
  data() {
    return {
      nav: [],
      baseNav: [],
      showSupportMenu: false,
      showUserMenu: false,
      showCompactUserMenu: false,
      sidebarElement: null,
      bodyObserver: null,
      dropdownObserver: null, // Observer para controlar dropdowns
      allowDropdownOpen: true, // Flag para permitir/bloquear abertura de dropdowns
      activeCollapsedDropdown: null,
      isLightTheme: false, // false = dark theme (default), true = light theme
      themeStyleInterval: null,
      menuNotificationInterval: null,
      menuNotificationCounts: {
        complaints: 0,
        qas: 0,
        dsar: 0,
        ombudsman: 0,
        support: 0,
      },
      isDesktopViewport: false,
      protocolSearch: "",
    };
  },
  computed: {
    ...mapGetters(["addRouters", "profile", "sidebar"]),
    ...mapGetters(["appSettings", "portalBucket"]),
    logo() {
      if (this.appSettings?.whitelabel) {
        return `https://${this.portalBucket}/${this.appSettings?.whitelabel.logo}`;
      }
      return "img/logo.png";
    },
    name() {
      return this.$route.name;
    },
    currentRoute() {
      return this.$route.path;
    },

    isMasterUser() {
      return this.profile.role === "root";
    },
    hasComplaintsModule() {
      if (this.isMasterUser) {
        return true;
      }
      if (this.profile.companyData) {
        const { modules } = this.profile.companyData;
        return modules.complaint && modules.complaint.enabled;
      }
      return false;
    },
    hasQaSModule() {
      if (this.isMasterUser) {
        return true;
      }
      if (this.profile.companyData) {
        const { modules } = this.profile.companyData;
        return modules.qas && modules.qas.enabled;
      }
      return false;
    },
    showSidebarExtras() {
      return !this.isSidebarMinimized;
    },
    isSidebarMinimized() {
      return this.isDesktopViewport && this.sidebar && !this.sidebar.opened;
    },
  },
  watch: {
    currentRoute: {
      handler(newRoute, oldRoute) {
        this.showUserMenu = false;
        this.showCompactUserMenu = false;

        // Fechar todos os dropdowns quando a rota muda (mas não no carregamento inicial)
        if (oldRoute) {
          // Bloquear abertura automática de dropdowns durante navegação
          this.allowDropdownOpen = false;
          this.closeAllDropdowns();

          // Manter bloqueado por um tempo para evitar que CoreUI reabra
          setTimeout(() => {
            this.allowDropdownOpen = true;
          }, 1000);
        }
        this.updateActiveNavigation(newRoute);
      },
      immediate: true,
    },
    isSidebarMinimized(newValue, oldValue) {
      if (oldValue && !newValue) {
        this.closeAllDropdowns();
      }
    },
  },
  created() {
    const handleRoutes = (routes, parentPath = "", seenUrls = new Set()) => {
      const items = [];

      for (const route of routes) {
        if (route.hidden || route.redirect) {
          if (route.children) {
            items.push(...handleRoutes(route.children, parentPath, seenUrls));
          }
          continue;
        }

        // Construir a URL corretamente
        let url = "";

        // Se a rota começa com /, é uma rota absoluta
        if (route.path.startsWith("/")) {
          url = route.path;
        } else if (parentPath) {
          // Concatenar com o parentPath (remover barras duplicadas)
          const cleanParent = parentPath.replace(/\/$/, "");
          url = cleanParent + "/" + route.path;
        } else {
          // Primeira rota relativa
          url = "/" + route.path;
        }

        // Evitar itens duplicados com a mesma URL (ex: múltiplas rotas Dashboard para diferentes roles)
        if (seenUrls.has(url)) {
          continue;
        }
        seenUrls.add(url);

        const item = {
          name: route.name,
          url: url,
        };

        if (route.meta) {
          const meta = route.meta;
          if (meta.icon) {
            item.icon = meta.icon;
          }
        }

        if (route.children && route.children.length > 0) {
          // Passar a URL completa como parentPath para os children
          const children = handleRoutes(route.children, url, seenUrls);
          // Só adicionar children se houver itens válidos
          if (children && children.length > 0) {
            item.children = children;
            // Para itens com children no CoreUI, precisamos adicionar a classe
            item.class = "nav-dropdown";
            // Para itens com children, não precisamos de URL no item pai
            // O CoreUI usa o item pai apenas como dropdown
            delete item.url;
          }
        }

        items.push(item);
      }
      return items;
    };

    const items = handleRoutes(this.addRouters);

    this.baseNav = items;
    this.rebuildNav();
  },
  mounted() {
    this.isDesktopViewport = window.innerWidth >= 992;

    // Ensure sidebar is visible on desktop and add necessary CoreUI classes
    const body = document.body;

    // Add CoreUI classes that might be missing
    body.classList.add("app", "sidebar-fixed");

    if (window.innerWidth >= 992) {
      // lg breakpoint
      body.classList.add("sidebar-show");
    } else {
      body.classList.remove("sidebar-show");
    }

    // Load saved theme preference
    this.loadThemePreference();

    // Handle window resize
    window.addEventListener("resize", this.handleResize);

    // Prevent sidebar from hiding on desktop when clicking outside
    this.preventSidebarHide();

    // Override CoreUI click handlers
    this.overrideCoreUIHandlers();

    // Force sidebar visibility check
    this.$nextTick(() => {
      this.sidebarElement = document.querySelector(".sidebar");

      // Fechar todos os dropdowns por padrão
      this.closeAllDropdowns();

      // Configurar observer para controlar dropdowns
      this.setupDropdownObserver();

      if (this.sidebarElement) {
        this.sidebarElement.addEventListener(
          "click",
          this.handleSidebarDropdownClick,
          true
        );
      }

      document.addEventListener("click", this.handleDocumentClick, true);

      // Update active navigation based on current route
      this.updateActiveNavigation(this.currentRoute);

      // Set up periodic theme style enforcement
      this.setupThemeStyleEnforcement();
    });

    this.syncSidebarStateClasses();
    this.loadMenuNotificationCounts();
    this.setupMenuNotificationRefresh();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);

    // Cleanup mutation observer
    if (this.bodyObserver) {
      this.bodyObserver.disconnect();
    }

    // Cleanup dropdown observer
    if (this.dropdownObserver) {
      this.dropdownObserver.disconnect();
    }

    if (this.sidebarElement) {
      this.sidebarElement.removeEventListener(
        "click",
        this.handleSidebarDropdownClick,
        true
      );
    }

    document.removeEventListener("click", this.handleDocumentClick, true);

    // Cleanup main click listener
    const mainElement = document.querySelector(".main");
    if (mainElement) {
      mainElement.removeEventListener("click", this.handleMainClick);
    }

    // Cleanup theme style interval
    if (this.themeStyleInterval) {
      clearInterval(this.themeStyleInterval);
    }

    if (this.menuNotificationInterval) {
      clearInterval(this.menuNotificationInterval);
    }

    document.body.classList.remove("sidebar-minimized");
  },
  methods: {
    rebuildNav() {
      this.nav = this.decorateNavItems(this.baseNav);
    },
    decorateNavItems(items = []) {
      return items.map((item) => {
        const nextItem = {
          ...item,
        };

        if (item.children && item.children.length > 0) {
          nextItem.children = this.decorateNavItems(item.children);
        }

        const counterKey = MENU_NOTIFICATION_ROUTE_MAP[item.url];
        const counter = counterKey
          ? this.menuNotificationCounts[counterKey]
          : 0;

        if (counter > 0) {
          nextItem.badge = {
            variant: "secondary",
            text: `${counter}`,
          };
        } else {
          delete nextItem.badge;
        }

        return nextItem;
      });
    },
    hasMenuItem(url, items = this.baseNav) {
      return items.some((item) => {
        if (item.url === url) {
          return true;
        }

        if (item.children && item.children.length > 0) {
          return this.hasMenuItem(url, item.children);
        }

        return false;
      });
    },
    getScopedCompanyId() {
      return this.isMasterUser ? undefined : this.profile.company;
    },
    getSupportNotificationCompanyId() {
      const isCompanyRootManager =
        this.profile &&
        this.profile.role === "company-root" &&
        Array.isArray(this.profile.managedCompanies) &&
        this.profile.managedCompanies.length > 0;

      if (this.isMasterUser || isCompanyRootManager) {
        return undefined;
      }

      return this.profile.company;
    },
    extractMenuNotificationCount(payload) {
      if (!payload) {
        return 0;
      }

      if (typeof payload.total === "number") {
        return payload.total;
      }

      if (typeof payload.totalCount === "number") {
        return payload.totalCount;
      }

      if (typeof payload.count === "number") {
        return payload.count;
      }

      if (typeof payload.totalDocs === "number") {
        return payload.totalDocs;
      }

      if (Array.isArray(payload.docs)) {
        return payload.docs.length;
      }

      if (Array.isArray(payload)) {
        return payload.length;
      }

      return 0;
    },
    async loadMenuNotificationCounts() {
      const company = this.getScopedCompanyId();
      const nextCounts = {
        complaints: 0,
        qas: 0,
        dsar: 0,
        ombudsman: 0,
        support: 0,
      };
      const requests = [];

      if (this.hasMenuItem("/denuncias")) {
        requests.push(
          getComplaints({
            page: 1,
            size: 1,
            filter: {
              status: ComplaintStatus.NEW,
              companyId: company,
            },
          })
            .then((response) => {
              nextCounts.complaints = this.extractMenuNotificationCount(
                response.data
              );
            })
            .catch((error) => {
              console.error("Erro ao carregar denuncias novas:", error);
            })
        );
      }

      if (this.hasMenuItem("/duvidas-e-sugestoes")) {
        requests.push(
          getQaSNotifications(company)
            .then((response) => {
              nextCounts.qas = this.extractMenuNotificationCount(response.data);
            })
            .catch((error) => {
              console.error("Erro ao carregar notificacoes de QaS:", error);
            })
        );
      }

      if (this.hasMenuItem("/lgpd")) {
        requests.push(
          getDSARNotifications(company)
            .then((response) => {
              nextCounts.dsar = this.extractMenuNotificationCount(
                response.data
              );
            })
            .catch((error) => {
              console.error("Erro ao carregar notificacoes de LGPD:", error);
            })
        );
      }

      if (this.hasMenuItem("/ouvidoria")) {
        requests.push(
          getOmbudsmanNotifications(company)
            .then((response) => {
              nextCounts.ombudsman = this.extractMenuNotificationCount(
                response.data
              );
            })
            .catch((error) => {
              console.error(
                "Erro ao carregar notificacoes de ouvidoria:",
                error
              );
            })
        );
      }

      requests.push(
        getSupportNotifications(this.getSupportNotificationCompanyId())
          .then((response) => {
            nextCounts.support = this.extractMenuNotificationCount(
              response.data
            );
          })
          .catch((error) => {
            console.error("Erro ao carregar notificacoes de suporte:", error);
          })
      );

      await Promise.all(requests);
      this.menuNotificationCounts = nextCounts;
      this.rebuildNav();
    },
    setupMenuNotificationRefresh() {
      if (this.menuNotificationInterval) {
        clearInterval(this.menuNotificationInterval);
      }

      this.menuNotificationInterval = setInterval(() => {
        this.loadMenuNotificationCounts();
      }, MENU_NOTIFICATION_REFRESH_INTERVAL);
    },
    syncSidebarStateClasses() {
      const body = document.body;

      if (!body) {
        return;
      }

      if (this.isSidebarMinimized) {
        body.classList.add("sidebar-minimized");
      } else {
        body.classList.remove("sidebar-minimized");
      }

      if (this.isDesktopViewport) {
        body.classList.add("sidebar-show");
      }
    },
    setupDropdownObserver() {
      // Observar mudanças nas classes dos dropdowns para impedir abertura automática
      const sidebar = document.querySelector(".sidebar");
      if (!sidebar) return;

      this.dropdownObserver = new MutationObserver((mutations) => {
        if (!this.allowDropdownOpen) {
          mutations.forEach((mutation) => {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "class"
            ) {
              const target = mutation.target;
              if (
                target.classList.contains("nav-dropdown") &&
                target.classList.contains("open")
              ) {
                // Remover a classe 'open' se não é permitido abrir
                target.classList.remove("open");
              }
            }
          });
        }
      });

      // Observar todos os dropdowns existentes e futuros
      const dropdowns = sidebar.querySelectorAll(".nav-dropdown");
      dropdowns.forEach((dropdown) => {
        this.dropdownObserver.observe(dropdown, {
          attributes: true,
          attributeFilter: ["class"],
        });
      });

      // Também observar o sidebar para novos dropdowns
      this.dropdownObserver.observe(sidebar, {
        childList: true,
        subtree: true,
      });
    },

    closeAllDropdowns() {
      // Remover a classe 'open' de todos os dropdowns no sidebar imediatamente
      const dropdowns = document.querySelectorAll(".sidebar .nav-dropdown");
      dropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        dropdown.classList.remove("collapsed-popover-open");
      });

      this.activeCollapsedDropdown = null;
    },
    getCollapsedPanelLink(target, panel) {
      if (!target || !panel) {
        return null;
      }

      const candidate = target.closest(".nav-link, a[href], [href], [to]");
      if (!candidate || !panel.contains(candidate)) {
        return null;
      }

      return candidate;
    },
    normalizeSidebarNavigationTarget(rawTarget) {
      if (!rawTarget || typeof rawTarget !== "string") {
        return null;
      }

      const trimmedTarget = rawTarget.trim();
      if (!trimmedTarget || trimmedTarget === "#") {
        return null;
      }

      if (
        trimmedTarget.startsWith("http://") ||
        trimmedTarget.startsWith("https://") ||
        trimmedTarget.startsWith("mailto:") ||
        trimmedTarget.startsWith("tel:")
      ) {
        return null;
      }

      if (trimmedTarget.startsWith("#/")) {
        return trimmedTarget.slice(1);
      }

      if (trimmedTarget.startsWith("#")) {
        return trimmedTarget.slice(1) || null;
      }

      if (trimmedTarget.startsWith("/")) {
        return trimmedTarget;
      }

      try {
        const parsedTarget = new URL(trimmedTarget, window.location.origin);
        if (parsedTarget.origin !== window.location.origin) {
          return null;
        }

        if (parsedTarget.hash && parsedTarget.hash.startsWith("#/")) {
          return parsedTarget.hash.slice(1);
        }

        return `${parsedTarget.pathname}${parsedTarget.search}${parsedTarget.hash}`;
      } catch (error) {
        return trimmedTarget;
      }
    },
    resolveCollapsedSidebarTarget(link) {
      if (!link) {
        return null;
      }

      const directTarget = this.normalizeSidebarNavigationTarget(
        link.getAttribute("href") || link.getAttribute("to")
      );
      if (directTarget) {
        return directTarget;
      }

      const navItem = link.closest(".nav-item");
      const vueInstance = link.__vue__ || (navItem && navItem.__vue__);
      const vueTarget = vueInstance
        ? vueInstance.to ||
          (vueInstance.$attrs && vueInstance.$attrs.to) ||
          (vueInstance.$props && vueInstance.$props.to) ||
          (vueInstance.item && vueInstance.item.url) ||
          (vueInstance.$props &&
            vueInstance.$props.item &&
            vueInstance.$props.item.url)
        : null;

      return this.normalizeSidebarNavigationTarget(vueTarget);
    },
    navigateCollapsedSidebarTarget(target) {
      if (!target) {
        return;
      }

      const navigation = this.$router.push(target);
      if (navigation && typeof navigation.catch === "function") {
        navigation.catch((error) => {
          if (error && error.name !== "NavigationDuplicated") {
            console.error("Erro ao navegar pelo menu retraido:", error);
          }
        });
      }
    },
    handleDocumentClick(event) {
      if (!this.isSidebarMinimizedInDom()) {
        return;
      }

      const clickedInsideSidebar = event.target.closest(".sidebar");
      if (!clickedInsideSidebar) {
        this.closeAllDropdowns();
      }
    },
    handleSidebarDropdownClick(event) {
      if (!this.isSidebarMinimizedInDom()) {
        return;
      }

      const clickedInsidePanel = event.target.closest(".nav-dropdown-items");
      if (clickedInsidePanel) {
        const link = this.getCollapsedPanelLink(
          event.target,
          clickedInsidePanel
        );
        if (!link) {
          return;
        }

        const target = this.resolveCollapsedSidebarTarget(link);
        if (target) {
          event.preventDefault();
          event.stopPropagation();
          this.closeAllDropdowns();
          this.navigateCollapsedSidebarTarget(target);
        } else {
          this.closeAllDropdowns();
        }
        return;
      }

      const trigger = event.target.closest(".nav-dropdown > .nav-link");

      if (!trigger) {
        this.closeAllDropdowns();
        return;
      }

      const dropdown = trigger.closest(".nav-dropdown");
      if (!dropdown) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const shouldOpen = this.activeCollapsedDropdown !== dropdown;
      this.closeAllDropdowns();

      if (shouldOpen) {
        dropdown.classList.add("collapsed-popover-open");
        this.activeCollapsedDropdown = dropdown;
      }
    },

    async logout() {
      try {
        // IMPORTANTE: Chamar a API de logout primeiro para limpar o cookie de sessão no servidor
        try {
          await logoutApi();
        } catch (apiError) {
          console.warn("Erro ao chamar API de logout:", apiError);
          // Continuar mesmo com erro na API
        }

        // Limpar dados locais
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        sessionStorage.clear();

        // Limpar cookies localmente (incluindo scsid)
        document.cookie.split(";").forEach(function (c) {
          const eqPos = c.indexOf("=");
          const name = eqPos > -1 ? c.substr(0, eqPos) : c;
          document.cookie =
            name.trim() + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        });

        // Limpar store se existir
        if (this.$store && this.$store.dispatch) {
          try {
            await this.$store.dispatch("LogOut");
          } catch (storeError) {
            // Store logout não disponível, continuando
          }
        }

        // Limpar headers do axios se existir
        if (axios.defaults.headers.common["Authorization"]) {
          delete axios.defaults.headers.common["Authorization"];
        }

        // Redirecionar para login
        window.location.href = "/admin/";
      } catch (error) {
        console.error("Erro durante logout:", error);
        // Mesmo com erro, tentar redirecionar
        window.location.href = "/admin/";
      }
    },
    toggleUserMenu() {
      this.showCompactUserMenu = false;
      this.showUserMenu = !this.showUserMenu;
    },
    toggleCompactUserMenu() {
      this.showUserMenu = false;
      this.showCompactUserMenu = !this.showCompactUserMenu;
    },
    toggleSidebarMinimize() {
      if (!this.isDesktopViewport) {
        return;
      }

      this.showUserMenu = false;
      this.showCompactUserMenu = false;
      this.closeAllDropdowns();

      this.$store.dispatch("toggleSideBar");

      this.$nextTick(() => {
        this.syncSidebarStateClasses();
        this.updateActiveNavigation(this.currentRoute);
      });
    },
    async onProtocolSearch() {
      if (!this.protocolSearch) return;
      try {
        await this.openProtocol(this.protocolSearch);
        this.protocolSearch = ""; // Limpar busca após sucesso
      } catch (error) {
        console.error("Erro ao buscar protocolo:", error);
        this.$bvToast.toast("Erro ao buscar protocolo.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
      }
    },
    toggleTheme(theme) {
      if (theme === "light") {
        this.isLightTheme = true;
      } else if (theme === "dark") {
        this.isLightTheme = false;
      }

      const body = document.body;
      const sidebar = document.querySelector(".sidebar");

      console.log("Toggle theme called, isLightTheme:", this.isLightTheme);
      console.log("Sidebar element:", sidebar);

      // First, clean up any existing inline styles and event listeners
      this.cleanupThemeStyles();

      if (this.isLightTheme) {
        body.classList.add("sidebar-light-theme");
        body.classList.remove("sidebar-dark-theme");
        if (sidebar) {
          sidebar.classList.add("sidebar-light-theme");
          sidebar.classList.remove("sidebar-dark-theme");
        }
        console.log("Applied light theme");

        // Force update all navigation elements and icons
        this.$nextTick(() => {
          this.applyLightThemeStyles();
        });
      } else {
        body.classList.remove("sidebar-light-theme");
        body.classList.add("sidebar-dark-theme");
        if (sidebar) {
          sidebar.classList.remove("sidebar-light-theme");
          sidebar.classList.add("sidebar-dark-theme");
        }
        console.log("Applied dark theme");

        // Reset to default dark theme
        this.$nextTick(() => {
          this.resetToDarkTheme();
        });
      }

      // Save theme preference to localStorage
      localStorage.setItem(
        "sidebarTheme",
        this.isLightTheme ? "light" : "dark"
      );
      console.log(
        "Theme saved to localStorage:",
        this.isLightTheme ? "light" : "dark"
      );

      // Force apply styles to ensure all items are visible
      this.$nextTick(() => {
        this.forceApplyThemeStyles();
      });
    },

    // Force apply theme styles to ensure visibility
    forceApplyThemeStyles() {
      if (this.isLightTheme) {
        this.applyLightThemeStyles();
      } else {
        this.applyActiveStylesDark();
      }

      // Also update active navigation to ensure proper styling
      this.updateActiveNavigation(this.currentRoute);
    },

    // Set up periodic theme style enforcement
    setupThemeStyleEnforcement() {
      // Clear any existing interval
      if (this.themeStyleInterval) {
        clearInterval(this.themeStyleInterval);
      }

      // Set up new interval to enforce theme styles every 3 seconds
      this.themeStyleInterval = setInterval(() => {
        this.forceApplyThemeStyles();
      }, 3000);
    },
    loadThemePreference() {
      const savedTheme = localStorage.getItem("sidebarTheme");
      if (savedTheme) {
        this.isLightTheme = savedTheme === "light";
        // Apply theme without triggering toggle (to avoid event listeners)
        if (this.isLightTheme) {
          this.applyLightThemeDirectly();
        } else {
          this.applyDarkThemeDirectly();
        }
      } else {
        // Default to dark theme
        this.isLightTheme = false;
        this.applyDarkThemeDirectly();
      }

      // Force apply styles to ensure visibility
      this.$nextTick(() => {
        this.forceApplyThemeStyles();
      });
    },

    applyLightThemeDirectly() {
      const body = document.body;
      const sidebar = document.querySelector(".sidebar");

      body.classList.add("sidebar-light-theme");
      body.classList.remove("sidebar-dark-theme");
      if (sidebar) {
        sidebar.classList.add("sidebar-light-theme");
        sidebar.classList.remove("sidebar-dark-theme");
      }

      this.$nextTick(() => {
        this.applyLightThemeStyles();
        // Update active styles for light theme
        this.updateActiveNavigation(this.currentRoute);
      });
    },

    applyDarkThemeDirectly() {
      const body = document.body;
      const sidebar = document.querySelector(".sidebar");

      body.classList.remove("sidebar-light-theme");
      body.classList.add("sidebar-dark-theme");
      if (sidebar) {
        sidebar.classList.remove("sidebar-light-theme");
        sidebar.classList.add("sidebar-dark-theme");
      }

      this.$nextTick(() => {
        this.resetToDarkTheme();
        // Update active styles for dark theme
        this.updateActiveNavigation(this.currentRoute);
      });
    },
    toggleSidebar() {
      // Toggle sidebar visibility for mobile
      const body = document.body;
      if (body.classList.contains("sidebar-show")) {
        body.classList.remove("sidebar-show");
      } else {
        body.classList.add("sidebar-show");
      }
    },
    handleResize() {
      const body = document.body;
      this.isDesktopViewport = window.innerWidth >= 992;

      if (!this.isDesktopViewport) {
        this.showCompactUserMenu = false;
      }

      if (window.innerWidth >= 992) {
        // lg breakpoint
        body.classList.add("sidebar-show");
      } else {
        body.classList.remove("sidebar-show");
        body.classList.remove("sidebar-minimized");
      }

      this.syncSidebarStateClasses();
    },
    preventSidebarHide() {
      // Monitor body class changes and prevent sidebar from hiding on desktop
      const body = document.body;
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            // If we're on desktop and sidebar-show was removed, add it back
            if (
              window.innerWidth >= 992 &&
              !body.classList.contains("sidebar-show")
            ) {
              body.classList.add("sidebar-show");
            }
          }
        });
      });

      observer.observe(body, {
        attributes: true,
        attributeFilter: ["class"],
      });

      // Store observer for cleanup
      this.bodyObserver = observer;

      // Also add click event listener to main content to prevent sidebar hiding
      this.$nextTick(() => {
        const mainElement = document.querySelector(".main");
        if (mainElement) {
          mainElement.addEventListener("click", this.handleMainClick);
        }
      });
    },
    handleMainClick(event) {
      // Prevent sidebar from hiding when clicking on main content on desktop
      if (window.innerWidth >= 992) {
        event.stopPropagation();
        const body = document.body;
        if (!body.classList.contains("sidebar-show")) {
          body.classList.add("sidebar-show");
        }
      }
    },
    overrideCoreUIHandlers() {
      // Override any CoreUI document click handlers that might hide the sidebar
      this.$nextTick(() => {
        document.addEventListener(
          "click",
          (event) => {
            // On desktop, prevent sidebar from hiding when clicking outside
            if (window.innerWidth >= 992) {
              const body = document.body;
              const sidebar = event.target.closest(".sidebar");

              // If not clicking on sidebar, ensure it stays visible on desktop
              if (!sidebar) {
                setTimeout(() => {
                  if (!body.classList.contains("sidebar-show")) {
                    body.classList.add("sidebar-show");
                  }
                }, 10);
              }
            }
          },
          true
        ); // Use capture phase to intercept before CoreUI handlers
      });
    },

    cleanupThemeStyles() {
      // Remove all inline styles (but NOT event listeners - they are needed for CoreUI dropdowns)
      const navItems = document.querySelectorAll(
        ".sidebar .nav-item, .sidebar .nav-link"
      );
      navItems.forEach((item) => {
        // Reset inline styles only
        item.style.color = "";
        item.style.backgroundColor = "";
        item.onmouseenter = null;
        item.onmouseleave = null;
        // NOTE: Do NOT clone/replace elements - it removes CoreUI event listeners for dropdowns
      });

      // Reset all icons
      const icons = document.querySelectorAll(
        '.sidebar .nav-icon, .sidebar .fa, .sidebar i, .sidebar [class*="fa-"]'
      );
      icons.forEach((icon) => {
        icon.style.color = "";
      });
    },

    isSidebarMinimizedInDom() {
      return document.body.classList.contains("sidebar-minimized");
    },

    applyLightThemeStyles() {
      const isMinimized = this.isSidebarMinimizedInDom();
      const navItems = document.querySelectorAll(
        ".sidebar .nav-item, .sidebar .nav-link"
      );
      navItems.forEach((item) => {
        if (isMinimized) {
          item.style.color = "";
          item.style.backgroundColor = "";
        } else {
          // Force ALL items to have visible text in light theme
          item.style.setProperty("color", "#000000", "important");
          item.style.setProperty(
            "background-color",
            "transparent",
            "important"
          );
        }

        item.onmouseenter = function () {
          if (!document.body.classList.contains("sidebar-light-theme")) {
            return;
          }

          if (document.body.classList.contains("sidebar-minimized")) {
            this.style.backgroundColor = "transparent";
            this.style.color = "#000000";
            return;
          }

          this.style.backgroundColor = "#e8e8e8";
          this.style.color = "#000000";
        };

        item.onmouseleave = function () {
          if (!document.body.classList.contains("sidebar-light-theme")) {
            return;
          }

          if (!this.classList.contains("active")) {
            this.style.backgroundColor = "transparent";
          }
          this.style.color = "#000000";
        };
      });

      // Force ALL icons to be black in light theme
      const icons = document.querySelectorAll(
        ".sidebar .nav-icon, .sidebar .fa, .sidebar i"
      );
      icons.forEach((icon) => {
        icon.style.setProperty("color", "#000000", "important");
      });

      // Additional timeout to catch any late-loading icons
      setTimeout(() => {
        const allIcons = document.querySelectorAll(
          '.sidebar .nav-icon, .sidebar .fa, .sidebar i, .sidebar [class*="fa-"]'
        );
        allIcons.forEach((icon) => {
          icon.style.setProperty("color", "#000000", "important");
        });
      }, 100);
    },

    resetToDarkTheme() {
      // Reset to default dark theme - let CoreUI handle the styling
      const navItems = document.querySelectorAll(
        ".sidebar .nav-item, .sidebar .nav-link"
      );
      navItems.forEach((item) => {
        item.style.color = "";
        item.style.backgroundColor = "";
      });

      // Reset all icons to default
      const icons = document.querySelectorAll(
        '.sidebar .nav-icon, .sidebar .fa, .sidebar i, .sidebar [class*="fa-"]'
      );
      icons.forEach((icon) => {
        icon.style.color = "";
      });
    },

    updateActiveNavigation(currentRoute) {
      // Remove active class from all navigation items
      const allNavItems = document.querySelectorAll(
        ".sidebar .nav-item, .sidebar .nav-link"
      );
      allNavItems.forEach((item) => {
        item.classList.remove("active");
      });

      // Find and activate the current route
      if (currentRoute === "/" || currentRoute === "/dashboard") {
        // Dashboard is the default route - find it by text content
        const allNavItems = document.querySelectorAll(".sidebar .nav-item");
        let dashboardItem = null;

        allNavItems.forEach((item) => {
          const text = item.textContent || "";
          if (text.toLowerCase().includes("dashboard")) {
            dashboardItem = item;
          }
        });

        if (dashboardItem) {
          dashboardItem.classList.add("active");
          const navLink = dashboardItem.querySelector(".nav-link");
          if (navLink) {
            navLink.classList.add("active");
          }
        }
      } else {
        // Find the matching route
        const matchingItem = Array.from(allNavItems).find((item) => {
          const href =
            item.getAttribute("href") ||
            item.querySelector("a")?.getAttribute("href");
          return href && currentRoute.includes(href.replace(/^\//, ""));
        });

        if (matchingItem) {
          matchingItem.classList.add("active");
          if (matchingItem.classList.contains("nav-link")) {
            matchingItem.classList.add("active");
          } else {
            const navLink = matchingItem.querySelector(".nav-link");
            if (navLink) {
              navLink.classList.add("active");
            }
          }
        }
      }

      // Force update active styles based on current theme
      this.$nextTick(() => {
        if (this.isLightTheme) {
          this.applyActiveStylesLight();
        } else {
          this.applyActiveStylesDark();
        }
      });

      // Also update active styles after a short delay to ensure DOM is ready
      setTimeout(() => {
        if (this.isLightTheme) {
          this.applyActiveStylesLight();
        } else {
          this.applyActiveStylesDark();
        }
      }, 200);
    },

    applyActiveStylesLight() {
      const isMinimized = this.isSidebarMinimizedInDom();
      // FIRST: Apply styles to ALL navigation items to ensure visibility
      const allNavItems = document.querySelectorAll(
        ".sidebar .nav-item, .sidebar .nav-link, .sidebar .sidebar-nav .nav-item, .sidebar .navbar-nav .nav-item"
      );
      allNavItems.forEach((item) => {
        if (isMinimized) {
          item.style.color = "";
          item.style.backgroundColor = "";
        } else {
          // Force ALL items to have visible text in light theme
          item.style.setProperty("color", "#000000", "important");
          item.style.setProperty(
            "background-color",
            "transparent",
            "important"
          );
        }
      });

      // SECOND: Style active items specifically
      const activeItems = document.querySelectorAll(
        ".sidebar .nav-item.active, .sidebar .nav-link.active, .sidebar .sidebar-nav .nav-item.active, .sidebar .navbar-nav .nav-item.active"
      );
      activeItems.forEach((item) => {
        if (!isMinimized) {
          item.style.setProperty("background-color", "#f5f5f5", "important");
          item.style.setProperty("color", "#000000", "important");
        }

        // Also ensure the parent nav-item is styled
        const parentNavItem = item.closest(".nav-item");
        if (parentNavItem && !isMinimized) {
          parentNavItem.style.setProperty(
            "background-color",
            "#f5f5f5",
            "important"
          );
          parentNavItem.style.setProperty("color", "#000000", "important");
        }
      });

      // THIRD: Force ALL icons to be black in light theme
      const allIcons = document.querySelectorAll(
        '.sidebar .nav-icon, .sidebar .fa, .sidebar i, .sidebar [class*="fa-"]'
      );
      allIcons.forEach((icon) => {
        icon.style.setProperty("color", "#000000", "important");
      });

      // FOURTH: Additional timeout to catch any late-loading icons
      setTimeout(() => {
        const allIcons = document.querySelectorAll(
          '.sidebar .nav-icon, .sidebar .fa, .sidebar i, .sidebar [class*="fa-"]'
        );
        allIcons.forEach((icon) => {
          icon.style.setProperty("color", "#000000", "important");
        });
      }, 100);
    },

    applyActiveStylesDark() {
      const isMinimized = this.isSidebarMinimizedInDom();
      // FIRST: Apply styles to ALL navigation items to ensure visibility
      const allNavItems = document.querySelectorAll(
        ".sidebar .nav-item, .sidebar .nav-link, .sidebar .sidebar-nav .nav-item, .sidebar .navbar-nav .nav-item"
      );
      allNavItems.forEach((item) => {
        if (isMinimized) {
          item.style.color = "";
          item.style.backgroundColor = "";
        } else {
          // Force ALL items to have visible text in dark theme
          item.style.setProperty(
            "color",
            "rgba(255, 255, 255, 0.9)",
            "important"
          );
          item.style.setProperty(
            "background-color",
            "transparent",
            "important"
          );
        }
      });

      // SECOND: Style active items specifically
      const activeItems = document.querySelectorAll(
        ".sidebar .nav-item.active, .sidebar .nav-link.active, .sidebar .sidebar-nav .nav-item.active, .sidebar .navbar-nav .nav-item.active"
      );
      activeItems.forEach((item) => {
        if (!isMinimized) {
          item.style.setProperty(
            "background-color",
            "rgba(255, 255, 255, 0.1)",
            "important"
          );
          item.style.setProperty(
            "color",
            "rgba(255, 255, 255, 1)",
            "important"
          );
        }

        // Also ensure the parent nav-item is styled
        const parentNavItem = item.closest(".nav-item");
        if (parentNavItem && !isMinimized) {
          parentNavItem.style.setProperty(
            "background-color",
            "rgba(255, 255, 255, 0.1)",
            "important"
          );
          parentNavItem.style.setProperty(
            "color",
            "rgba(255, 255, 255, 1)",
            "important"
          );
        }
      });

      // THIRD: Force ALL icons to be white in dark theme
      const allIcons = document.querySelectorAll(
        '.sidebar .nav-icon, .sidebar .fa, .sidebar i, .sidebar [class*="fa-"]'
      );
      allIcons.forEach((icon) => {
        icon.style.setProperty(
          "color",
          "rgba(255, 255, 255, 0.9)",
          "important"
        );
      });

      // FOURTH: Additional timeout to catch any late-loading icons
      setTimeout(() => {
        const allIcons = document.querySelectorAll(
          '.sidebar .nav-icon, .sidebar .fa, .sidebar i, .sidebar [class*="fa-"]'
        );
        allIcons.forEach((icon) => {
          icon.style.setProperty(
            "color",
            "rgba(255, 255, 255, 0.9)",
            "important"
          );
        });
      }, 100);
    },
  },
};
</script>

<style scoped>
/* Mobile Toggle Button */
.mobile-sidebar-toggle {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  backdrop-filter: blur(10px);
}

.sidebar-toggle-btn {
  color: #333;
  padding: 0.5rem;
  border: none;
  background: transparent;
  font-size: 1.2rem;
}

.sidebar-toggle-btn:hover,
.sidebar-toggle-btn:focus {
  color: #007bff;
  text-decoration: none;
  box-shadow: none;
}

.sidebar-collapse-control {
  position: absolute;
  top: 1.4rem;
  right: -0.95rem;
  z-index: 1002;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-collapse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.95rem;
  height: 1.95rem;
  padding: 0;
  border-radius: 999px;
  color: #5f6773;
  background: #f7f7f8;
  border: 1px solid rgba(31, 41, 55, 0.08);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease, color 0.2s ease;
}

.sidebar-collapse-btn:hover,
.sidebar-collapse-btn:focus {
  color: #313842;
  background: #ffffff;
  text-decoration: none;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.2);
  transform: translateX(1px);
}

.sidebar-collapse-btn .fa {
  font-size: 0.9rem;
  font-weight: 700;
}

.sidebar-dark-theme .sidebar-collapse-btn {
  color: rgba(255, 255, 255, 0.8);
  background: #5c636b;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.3);
}

.sidebar-dark-theme .sidebar-collapse-btn:hover,
.sidebar-dark-theme .sidebar-collapse-btn:focus {
  color: #ffffff;
  background: #6a727b;
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.36);
}

/* Logo Section Styles */
.sidebar-logo-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  position: relative;
  transition: all 0.3s ease;
  min-height: 70px;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  text-decoration: none;
  flex: 1;
  justify-content: center;
}

.sidebar-brand-full {
  max-height: 40px;
  max-width: 140px;
  display: block;
}

.sidebar-user-section {
  padding: 0;
  border-top: none;
  position: relative;
}

.sidebar-user-section.is-compact {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0 0.85rem;
}

.sidebar-top-divider {
  height: 1px;
  margin: 0 1rem 0.35rem;
  background: rgba(255, 255, 255, 0.1);
}

.user-profile-dropdown {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  width: 100%;
}

.user-profile-dropdown:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-greeting {
  color: rgba(255, 255, 255, 0.64);
  font-size: 0.76rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 0.3rem;
}

.user-name {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.98rem;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-arrow {
  color: rgba(255, 255, 255, 0.7);
  transition: transform 0.2s ease;
  flex-shrink: 0;
  margin-left: auto;
}

.dropdown-arrow .fa-chevron-down.rotated {
  transform: rotate(180deg);
}

.user-actions-dropdown {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0;
  margin-top: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  margin: 0.5rem 1rem;
}

.sidebar-logout-section {
  margin-top: auto;
  padding: 0 0 1rem;
}

.sidebar-logout-section.is-compact {
  display: flex;
  justify-content: center;
  padding: 0 0 1rem;
}

.user-logout-action {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  width: calc(100% - 2rem);
  min-height: 2.85rem;
  margin: 0 1rem;
}

.user-action-btn {
  text-align: left;
  padding: 0.5rem 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  border: none;
  background: transparent;
}

.user-action-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 1);
  text-decoration: none;
}

.logout-btn {
  color: #ffd0d0;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(248, 113, 113, 0.18);
  border-radius: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.logout-btn:hover {
  background-color: rgba(239, 68, 68, 0.2);
  color: #ffe1e1;
  border-color: rgba(252, 165, 165, 0.28);
}

.logout-btn:focus {
  background-color: rgba(239, 68, 68, 0.18);
  color: #ffe1e1;
  border-color: rgba(252, 165, 165, 0.3);
  box-shadow: none;
}

.logout-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  color: inherit;
}

.logout-btn-icon i {
  font-size: 0.95rem;
}

.logout-btn-label {
  line-height: 1;
}

.user-compact-actions {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
}

.user-compact-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 12px;
  background: transparent;
  color: rgba(255, 255, 255, 0.92);
  border: none !important;
  box-shadow: none !important;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.user-compact-action:hover,
.user-compact-action:focus,
.user-compact-action.is-open {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  text-decoration: none;
}

.user-compact-logout {
  color: #e55353;
}

.user-compact-logout:hover,
.user-compact-logout:focus {
  background: rgba(229, 83, 83, 0.12);
  color: #ff8c8c;
}

.user-compact-panel {
  position: absolute;
  right: -10rem;
  bottom: 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 9rem;
  padding: 0.55rem;
  background: #39414a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.3);
  z-index: 1003;
}

.compact-panel-btn {
  justify-content: flex-start;
  width: 100%;
}

/* Ensure sidebar is visible */
.app-body {
  display: flex;
}

.sidebar {
  min-height: 100vh;
}

/* Force sidebar visibility on desktop */
@media (min-width: 992px) {
  .sidebar {
    display: flex !important;
    margin-left: 0 !important;
  }

  /* Override CoreUI classes that might hide the sidebar */
  .app .sidebar {
    transform: translateX(0) !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
}

/* Additional CoreUI fixes */
.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Remove any automatic borders from CoreUI components */
.sidebar * {
  border: none !important;
}

.sidebar .nav-link,
.sidebar .nav-item,
.sidebar .sidebar-nav {
  border: none !important;
  border-bottom: none !important;
  border-top: none !important;
}

/* Theme Toggle Styles */
.sidebar-theme-section {
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 0;
}

.sidebar-theme-section.is-compact {
  display: flex;
  justify-content: center;
  padding: 0.85rem 0.5rem;
}

.theme-toggle-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme-switch {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
  padding: 0.25rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
}

.theme-option-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  flex: 1;
  min-height: 2.25rem;
  padding: 0.45rem 0.75rem;
  border-radius: 10px;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.88rem;
  font-weight: 600;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.theme-option-btn:hover,
.theme-option-btn:focus {
  color: rgba(255, 255, 255, 0.96);
  text-decoration: none;
  box-shadow: none;
}

.theme-option-btn.is-active {
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
}

.theme-option-icon {
  font-size: 0.9rem;
  width: 0.95rem;
  text-align: center;
}

.theme-compact-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.3rem;
  height: 2.3rem;
  padding: 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: none;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.theme-compact-btn:hover,
.theme-compact-btn:focus {
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  text-decoration: none;
  box-shadow: none;
  transform: translateY(-1px);
}

.app-body {
  display: flex;
  flex-direction: row;
  flex: 1;
}

.sidebar {
  display: flex;
  flex-direction: column;
  width: 200px;
  min-height: 100vh;
  background-color: #2f353a;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  transition: margin-left 0.2s;
}

.app-sidebar-minimized .sidebar {
  width: 64px;
  overflow: visible !important;
  --collapsed-submenu-offset: 1.35rem;
}

.app-sidebar-minimized .main {
  margin-left: 64px;
}

.app-sidebar-minimized .sidebar-collapse-control {
  right: -0.95rem;
}

.app-sidebar-minimized .sidebar .navbar-nav {
  overflow: visible;
}

.app-sidebar-minimized .sidebar /deep/ .sidebar-nav,
.app-sidebar-minimized .sidebar .navbar-nav {
  width: 100%;
  overflow: visible !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.app-sidebar-minimized .sidebar /deep/ .sidebar-nav,
.app-sidebar-minimized .sidebar /deep/ .sidebar-nav .nav,
.app-sidebar-minimized .sidebar .sidebar-nav,
.app-sidebar-minimized .sidebar .sidebar-nav .nav {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  overflow: visible !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin: 0 !important;
  list-style: none !important;
}

.app-sidebar-minimized .sidebar /deep/ .nav-item,
.app-sidebar-minimized .sidebar .navbar-nav .nav-item {
  display: flex;
  justify-content: center;
  width: 100%;
  position: relative;
}

.app-sidebar-minimized .sidebar /deep/ .nav-link,
.app-sidebar-minimized .sidebar .navbar-nav .nav-link {
  display: flex;
  align-items: center;
  position: relative;
  justify-content: center;
  font-size: 0;
  width: 2.6rem;
  min-width: 2.6rem;
  max-width: 2.6rem;
  height: 2.6rem;
  margin: 0.18rem auto;
  padding: 0.85rem 0 !important;
  text-align: center;
  overflow: visible;
  border-radius: 12px;
  background: transparent !important;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.app-sidebar-minimized .sidebar /deep/ .sidebar-nav .nav-link,
.app-sidebar-minimized .sidebar .sidebar-nav .nav-link {
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin-left: auto !important;
  margin-right: auto !important;
}

.app-sidebar-minimized .sidebar /deep/ .nav-icon,
.app-sidebar-minimized .sidebar .navbar-nav .nav-icon {
  position: absolute;
  left: 50%;
  top: 50%;
  display: block;
  width: auto !important;
  margin-right: 0 !important;
  margin-left: 0 !important;
  font-size: 1rem;
  text-align: center;
  transform: translate(-50%, -50%);
}

.app-sidebar-minimized .sidebar /deep/ .sidebar-nav .nav-link > .nav-icon,
.app-sidebar-minimized .sidebar .sidebar-nav .nav-link > .nav-icon,
.app-sidebar-minimized .sidebar .navbar-nav .nav-link > .nav-icon {
  flex: 0 0 auto;
  position: absolute;
  left: 50%;
  top: 50%;
  width: auto !important;
  margin-right: 0 !important;
  margin-left: 0 !important;
  transform: translate(-50%, -50%);
}

.app-sidebar-minimized .sidebar /deep/ .nav-link .fa,
.app-sidebar-minimized .sidebar /deep/ .nav-link i,
.app-sidebar-minimized .sidebar .navbar-nav .nav-link .fa,
.app-sidebar-minimized .sidebar .navbar-nav .nav-link i {
  position: absolute;
  left: 50%;
  top: 50%;
  display: block;
  width: auto;
  margin-right: 0;
  margin-left: 0;
  text-align: center;
  transform: translate(-50%, -50%);
}

.app-sidebar-minimized .sidebar /deep/ .nav-link::after,
.app-sidebar-minimized .sidebar .navbar-nav .nav-link::after {
  display: none;
}

.app-sidebar-minimized .sidebar /deep/ .scroll-area,
.app-sidebar-minimized .sidebar /deep/ .ps {
  overflow: visible !important;
}

.app-sidebar-minimized .sidebar /deep/ .nav-dropdown,
.app-sidebar-minimized .sidebar .sidebar-nav .nav-dropdown {
  position: relative;
}

.app-sidebar-minimized .sidebar /deep/ .nav-dropdown.collapsed-popover-open,
.app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown.collapsed-popover-open {
  overflow: visible !important;
}

.app-sidebar-minimized .sidebar /deep/ .nav-dropdown > .nav-dropdown-items,
.app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown
  > .nav-dropdown-items {
  position: absolute;
  left: calc(100% + var(--collapsed-submenu-offset));
  top: 0;
  display: none !important;
  min-width: 12rem;
  padding: 0.65rem;
  background: #39414a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.32);
  overflow: visible !important;
  z-index: 1010;
  pointer-events: auto;
}

.app-sidebar-minimized .sidebar /deep/ .nav-dropdown.open > .nav-dropdown-items,
.app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown.open
  > .nav-dropdown-items {
  display: none !important;
}

.app-sidebar-minimized
  .sidebar
  /deep/
  .nav-dropdown.collapsed-popover-open
  > .nav-dropdown-items,
.app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown.collapsed-popover-open
  > .nav-dropdown-items {
  display: block !important;
}

.app-sidebar-minimized
  .sidebar
  /deep/
  .nav-dropdown:hover
  > .nav-dropdown-items,
.app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown:hover
  > .nav-dropdown-items,
.app-sidebar-minimized
  .sidebar
  /deep/
  .nav-dropdown.collapsed-popover-open:hover
  > .nav-dropdown-items,
.app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown.collapsed-popover-open:hover
  > .nav-dropdown-items {
  left: calc(100% + var(--collapsed-submenu-offset)) !important;
}

.app-sidebar-minimized .sidebar /deep/ .nav-dropdown-items .nav-item,
.app-sidebar-minimized .sidebar .sidebar-nav .nav-dropdown-items .nav-item {
  display: block;
  width: 100%;
}

.app-sidebar-minimized .sidebar /deep/ .nav-dropdown-items .nav-link,
.app-sidebar-minimized .sidebar .sidebar-nav .nav-dropdown-items .nav-link {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  height: auto;
  margin: 0;
  padding: 0.7rem 0.85rem !important;
  border-radius: 12px;
  font-size: 0.92rem;
  line-height: 1.2;
  text-align: left;
  color: rgba(255, 255, 255, 0.86) !important;
  background: transparent !important;
  transform: none !important;
}

.app-sidebar-minimized .sidebar /deep/ .nav-dropdown-items .nav-link:hover,
.app-sidebar-minimized .sidebar /deep/ .nav-dropdown-items .nav-link:focus,
.app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown-items
  .nav-link:hover,
.app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown-items
  .nav-link:focus {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #ffffff !important;
}

.app-sidebar-minimized .sidebar /deep/ .nav-dropdown-items .nav-link::after,
.app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown-items
  .nav-link::after {
  display: block;
  position: static;
  margin-left: auto;
  transform: none !important;
}

.app-sidebar-minimized .sidebar /deep/ .nav-dropdown-items .nav-icon,
.app-sidebar-minimized .sidebar /deep/ .nav-dropdown-items .fa,
.app-sidebar-minimized .sidebar /deep/ .nav-dropdown-items i,
.app-sidebar-minimized .sidebar .sidebar-nav .nav-dropdown-items .nav-icon,
.app-sidebar-minimized .sidebar .sidebar-nav .nav-dropdown-items .fa,
.app-sidebar-minimized .sidebar .sidebar-nav .nav-dropdown-items i {
  position: static;
  width: auto !important;
  margin: 0 0.7rem 0 0 !important;
  transform: none !important;
  font-size: 0.95rem;
  text-align: left;
}

.app-sidebar-minimized .sidebar /deep/ .nav-link:hover,
.app-sidebar-minimized .sidebar /deep/ .nav-link:focus,
.app-sidebar-minimized .sidebar /deep/ .nav-item:hover,
.app-sidebar-minimized .sidebar /deep/ .nav-item:focus,
.app-sidebar-minimized .sidebar .navbar-nav .nav-link:hover,
.app-sidebar-minimized .sidebar .navbar-nav .nav-link:focus,
.app-sidebar-minimized .sidebar .navbar-nav .nav-item:hover,
.app-sidebar-minimized .sidebar .navbar-nav .nav-item:focus {
  background: transparent !important;
  box-shadow: none !important;
  transform: none !important;
}

.sidebar /deep/ .nav-link,
.sidebar .navbar-nav .nav-link {
  position: relative;
}

.sidebar /deep/ .nav-link .badge,
.sidebar .navbar-nav .nav-link .badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.45rem;
  height: 1.45rem;
  padding: 0 0.42rem;
  margin-left: auto;
  border-radius: 999px;
  background: #e8edf5 !important;
  color: #4f647d !important;
  border: 1px solid rgba(120, 138, 160, 0.22) !important;
  box-shadow: none !important;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
}

.app-sidebar-minimized .sidebar /deep/ .nav-link .badge,
.app-sidebar-minimized .sidebar .navbar-nav .nav-link .badge {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  width: 0.55rem;
  min-width: 0.55rem;
  height: 0.55rem;
  padding: 0;
  border-radius: 50%;
  background: #e55353 !important;
  border: 2px solid #2f353a !important;
  font-size: 0;
  line-height: 0;
}

/* Dark Theme (Default) */
.sidebar-dark-theme .sidebar {
  background-color: #2f353a !important;
}

.sidebar-dark-theme .app-sidebar-minimized .sidebar /deep/ .nav-link,
.sidebar-dark-theme .app-sidebar-minimized .sidebar .navbar-nav .nav-link,
.sidebar-dark-theme .app-sidebar-minimized .sidebar /deep/ .nav-link:hover,
.sidebar-dark-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-link:hover {
  background: transparent !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

.sidebar-dark-theme .app-sidebar-minimized .sidebar /deep/ .nav-link:hover,
.sidebar-dark-theme .app-sidebar-minimized .sidebar /deep/ .nav-link:focus,
.sidebar-dark-theme .app-sidebar-minimized .sidebar .navbar-nav .nav-link:hover,
.sidebar-dark-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-link:focus {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #ffffff !important;
}

.sidebar-dark-theme
  .app-sidebar-minimized
  .sidebar
  /deep/
  .nav-item.active
  .nav-link,
.sidebar-dark-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-item.active
  .nav-link,
.sidebar-dark-theme .app-sidebar-minimized .sidebar /deep/ .nav-link.active,
.sidebar-dark-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-link.active {
  background: rgba(255, 255, 255, 0.1) !important;
  color: #ffffff !important;
}

.sidebar-light-theme .sidebar-collapse-btn {
  color: #5f6773;
  background: #f7f7f8;
  border: 1px solid rgba(31, 41, 55, 0.08);
}

.sidebar-light-theme .sidebar-collapse-btn:hover,
.sidebar-light-theme .sidebar-collapse-btn:focus {
  color: #313842;
  background: #ffffff;
}

.sidebar-light-theme .sidebar /deep/ .nav-link .badge,
.sidebar-light-theme .sidebar .navbar-nav .nav-link .badge {
  background: #edf2f8 !important;
  color: #52657d !important;
  border: 1px solid rgba(120, 138, 160, 0.18) !important;
}

.sidebar-light-theme .app-sidebar-minimized .sidebar /deep/ .nav-link .badge,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-link
  .badge {
  background: #e55353 !important;
  border: 2px solid #ffffff !important;
}

.sidebar-light-theme .app-sidebar-minimized .sidebar /deep/ .nav-link,
.sidebar-light-theme .app-sidebar-minimized .sidebar .navbar-nav .nav-link,
.sidebar-light-theme .app-sidebar-minimized .sidebar /deep/ .nav-link:hover,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-link:hover {
  background: transparent !important;
  color: #000000 !important;
}

.sidebar-light-theme .app-sidebar-minimized .sidebar /deep/ .nav-link:hover,
.sidebar-light-theme .app-sidebar-minimized .sidebar /deep/ .nav-link:focus,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-link:hover,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-link:focus {
  background: #eef2f6 !important;
  color: #111827 !important;
}

.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  /deep/
  .nav-item.active
  .nav-link,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-item.active
  .nav-link,
.sidebar-light-theme .app-sidebar-minimized .sidebar /deep/ .nav-link.active,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .navbar-nav
  .nav-link.active {
  background: #eef2f6 !important;
  color: #111827 !important;
}

.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  /deep/
  .nav-dropdown
  > .nav-dropdown-items,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown
  > .nav-dropdown-items {
  background: #ffffff;
  border: 1px solid rgba(120, 138, 160, 0.16);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.14);
}

.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  /deep/
  .nav-dropdown-items
  .nav-link,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown-items
  .nav-link {
  color: #4b5563 !important;
}

.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  /deep/
  .nav-dropdown-items
  .nav-link:hover,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  /deep/
  .nav-dropdown-items
  .nav-link:focus,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown-items
  .nav-link:hover,
.sidebar-light-theme
  .app-sidebar-minimized
  .sidebar
  .sidebar-nav
  .nav-dropdown-items
  .nav-link:focus {
  background: #eef2f6 !important;
  color: #111827 !important;
}

.sidebar-dark-theme .sidebar-logo-section,
.sidebar-dark-theme .sidebar-theme-section,
.sidebar-dark-theme .sidebar-user-section {
  color: rgba(255, 255, 255, 0.9) !important;
}

.sidebar-dark-theme .user-greeting {
  color: rgba(255, 255, 255, 0.64) !important;
}

.sidebar-dark-theme .user-name,
.sidebar-dark-theme .user-email {
  color: rgba(255, 255, 255, 0.9) !important;
}

.sidebar-dark-theme .user-email {
  color: rgba(255, 255, 255, 0.7) !important;
}

.sidebar-dark-theme .theme-label {
  color: rgba(255, 255, 255, 0.8) !important;
}

/* Ensure only one item is selected in dark theme */
.sidebar-dark-theme .sidebar .nav-item.active,
.sidebar-dark-theme .sidebar .sidebar-nav .nav-item.active,
.sidebar-dark-theme .sidebar .navbar-nav .nav-item.active {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.sidebar-dark-theme .sidebar .nav-item.active .nav-link,
.sidebar-dark-theme .sidebar .sidebar-nav .nav-item.active .nav-link,
.sidebar-dark-theme .sidebar .navbar-nav .nav-item.active .nav-link {
  color: rgba(255, 255, 255, 1) !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
}

/* Light Theme */
.sidebar-light-theme .sidebar {
  background-color: #ffffff !important;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1) !important;
}

/* Force all text to be black in light theme */
.sidebar-light-theme .sidebar *,
.sidebar-light-theme .sidebar *::before,
.sidebar-light-theme .sidebar *::after {
  color: #000000 !important;
}

/* Override any CoreUI default colors */
body.sidebar-light-theme .sidebar,
body.sidebar-light-theme .sidebar *,
.sidebar-light-theme .sidebar .nav,
.sidebar-light-theme .sidebar .navbar-nav,
.sidebar-light-theme .sidebar .nav-item,
.sidebar-light-theme .sidebar .nav-link,
.sidebar-light-theme .sidebar .sidebar-nav,
.sidebar-light-theme .sidebar .sidebar-nav .nav-item,
.sidebar-light-theme .sidebar .sidebar-nav .nav-link {
  color: #000000 !important;
}

.sidebar-light-theme .sidebar-logo-section,
.sidebar-light-theme .sidebar-theme-section,
.sidebar-light-theme .sidebar-user-section {
  color: #000000 !important;
}

.sidebar-light-theme .user-greeting {
  color: #6b7280 !important;
}

.sidebar-light-theme .user-name,
.sidebar-light-theme .user-email {
  color: #000000 !important;
}

.sidebar-light-theme .user-email {
  color: #000000 !important;
}

.sidebar-light-theme .sidebar-top-divider {
  background: rgba(120, 138, 160, 0.18);
}

/* Navigation items in light theme */
.sidebar-light-theme .nav-link,
.sidebar-light-theme .sidebar .nav-link,
.sidebar-light-theme .sidebar .navbar-nav .nav-link,
.sidebar-light-theme .sidebar .sidebar-nav .nav-link {
  color: #000000 !important;
  background-color: transparent !important;
}

/* Stronger hover effects - override CoreUI blue */
.sidebar-light-theme .nav-link:hover,
.sidebar-light-theme .sidebar .nav-link:hover,
.sidebar-light-theme .sidebar .navbar-nav .nav-link:hover,
.sidebar-light-theme .sidebar .sidebar-nav .nav-link:hover,
.sidebar-light-theme .nav-item:hover,
.sidebar-light-theme .sidebar .nav-item:hover,
.sidebar-light-theme .sidebar .navbar-nav .nav-item:hover,
.sidebar-light-theme .sidebar .sidebar-nav .nav-item:hover,
.sidebar-light-theme .sidebar .nav .nav-item .nav-link:hover,
.sidebar-light-theme .sidebar .navbar-nav .nav-item .nav-link:hover,
.sidebar-light-theme .sidebar .sidebar-nav .nav-item .nav-link:hover {
  background-color: #e8e8e8 !important;
  color: #000000 !important;
  transition: background-color 0.2s ease !important;
  border-color: transparent !important;
}

/* Active states */
.sidebar-light-theme .nav-link.active,
.sidebar-light-theme .sidebar .nav-link.active,
.sidebar-light-theme .sidebar .navbar-nav .nav-link.active,
.sidebar-light-theme .sidebar .sidebar-nav .nav-link.active {
  background-color: #f5f5f5 !important;
  color: #000000 !important;
}

/* Active navigation item in light theme */
.sidebar-light-theme .nav-item.active,
.sidebar-light-theme .sidebar-nav .nav-item.active,
.sidebar-light-theme .nav-item.active .nav-link,
.sidebar-light-theme .sidebar-nav .nav-item.active .nav-link {
  background-color: #f5f5f5 !important;
  color: #000000 !important;
}

/* Ensure only one item is selected in light theme */
.sidebar-light-theme .sidebar .nav-item.active,
.sidebar-light-theme .sidebar .sidebar-nav .nav-item.active,
.sidebar-light-theme .sidebar .navbar-nav .nav-item.active {
  background-color: #f5f5f5 !important;
}

.sidebar-light-theme .sidebar .nav-item.active .nav-link,
.sidebar-light-theme .sidebar .sidebar-nav .nav-item.active .nav-link,
.sidebar-light-theme .sidebar .navbar-nav .nav-item.active .nav-link {
  color: #000000 !important;
  background-color: #f5f5f5 !important;
}

/* Force override for any active states */
.sidebar-light-theme .nav-item.active *,
.sidebar-light-theme .sidebar-nav .nav-item.active * {
  color: #000000 !important;
}

.sidebar-light-theme .sidebar-theme-section {
  border-top: 1px solid #e0e0e0 !important;
}

.sidebar-light-theme .theme-switch {
  background: #f1f4f7 !important;
  border: 1px solid rgba(120, 138, 160, 0.16) !important;
}

.sidebar-light-theme .theme-compact-btn {
  background: #eef2f6;
  color: #4e6278;
  border: 1px solid rgba(120, 138, 160, 0.18) !important;
}

.sidebar-light-theme .theme-compact-btn:hover,
.sidebar-light-theme .theme-compact-btn:focus {
  background: #e4ebf3;
  color: #223042;
}

.sidebar-light-theme .theme-option-btn {
  color: #65758b !important;
}

.sidebar-light-theme .theme-option-btn:hover,
.sidebar-light-theme .theme-option-btn:focus {
  color: #2d3748 !important;
}

.sidebar-light-theme .theme-option-btn.is-active {
  background: #ffffff !important;
  color: #1f2937 !important;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08) !important;
}

.sidebar-light-theme .user-compact-action {
  color: #334155;
}

.sidebar-light-theme .user-compact-action:hover,
.sidebar-light-theme .user-compact-action:focus,
.sidebar-light-theme .user-compact-action.is-open {
  background: #eef2f6;
  color: #111827;
}

.sidebar-light-theme .user-compact-logout {
  color: #dc3545;
}

.sidebar-light-theme .user-compact-logout:hover,
.sidebar-light-theme .user-compact-logout:focus {
  background: #fce8ea;
  color: #b42318;
}

.sidebar-light-theme .user-compact-panel {
  background: #ffffff;
  border: 1px solid rgba(120, 138, 160, 0.16);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.12);
}

/* User actions in light theme */
.sidebar-light-theme .user-action-btn {
  color: #000000 !important;
}

.sidebar-light-theme .user-action-btn:hover {
  background-color: #f5f5f5 !important;
  color: #000000 !important;
}

.sidebar-light-theme .logout-btn {
  color: #c2414d !important;
  background: #fff4f5 !important;
  border: 1px solid rgba(220, 53, 69, 0.12) !important;
}

.sidebar-light-theme .logout-btn:hover {
  background-color: #fdeced !important;
  color: #9f2230 !important;
  border-color: rgba(220, 53, 69, 0.2) !important;
}

.sidebar-light-theme .logout-btn:focus {
  background-color: #fdeced !important;
  color: #9f2230 !important;
  border-color: rgba(220, 53, 69, 0.22) !important;
}

.sidebar-light-theme .logout-btn-icon {
  background: #fddcdf !important;
  color: #c2414d !important;
}

/* Icons and navigation in light theme */
.sidebar-light-theme .nav-icon,
.sidebar-light-theme .fa,
.sidebar-light-theme i,
.sidebar-light-theme .sidebar .nav-icon,
.sidebar-light-theme .sidebar .fa,
.sidebar-light-theme .sidebar i,
.sidebar-light-theme .sidebar .nav-item .nav-icon,
.sidebar-light-theme .sidebar .nav-item .fa,
.sidebar-light-theme .sidebar .nav-item i,
.sidebar-light-theme .sidebar .nav-link .nav-icon,
.sidebar-light-theme .sidebar .nav-link .fa,
.sidebar-light-theme .sidebar .nav-link i {
  color: #000000 !important;
}

.sidebar-light-theme .nav-link:hover .nav-icon,
.sidebar-light-theme .nav-link:hover .fa,
.sidebar-light-theme .nav-link:hover i,
.sidebar-light-theme .sidebar .nav-link:hover .nav-icon,
.sidebar-light-theme .sidebar .nav-link:hover .fa,
.sidebar-light-theme .sidebar .nav-link:hover i {
  color: #000000 !important;
}

/* Manual link in light theme */
.sidebar-light-theme .navbar-nav .nav-item .nav-link {
  color: #000000 !important;
}

.sidebar-light-theme .navbar-nav .nav-item .nav-link:hover {
  background-color: #f5f5f5 !important;
  color: #000000 !important;
}

/* Dropdown arrow in light theme */
.sidebar-light-theme .dropdown-arrow {
  color: #000000 !important;
}

.sidebar-light-theme .dropdown-arrow .fa-chevron-down {
  color: #000000 !important;
}

/* Additional specific elements */
.sidebar-light-theme .sidebar .nav-title {
  color: #000000 !important;
}

.sidebar-light-theme .sidebar .nav-divider {
  border-color: #e0e0e0 !important;
}

/* Force override for any remaining elements */
.sidebar-light-theme .sidebar a,
.sidebar-light-theme .sidebar span,
.sidebar-light-theme .sidebar p,
.sidebar-light-theme .sidebar h1,
.sidebar-light-theme .sidebar h2,
.sidebar-light-theme .sidebar h3,
.sidebar-light-theme .sidebar h4,
.sidebar-light-theme .sidebar h5,
.sidebar-light-theme .sidebar h6 {
  color: #000000 !important;
}

/* Hover for all clickable elements */
.sidebar-light-theme .sidebar a:hover,
.sidebar-light-theme .sidebar .nav-item:hover a,
.sidebar-light-theme .sidebar .nav-link:hover,
.sidebar-light-theme .sidebar button:hover {
  background-color: #e8e8e8 !important;
  color: #000000 !important;
  transition: background-color 0.2s ease !important;
}

/* Ultra specific overrides for CoreUI elements */
body.sidebar-light-theme .app .sidebar .nav .nav-item .nav-link,
body.sidebar-light-theme .app .sidebar .navbar-nav .nav-item .nav-link,
body.sidebar-light-theme .app .sidebar .sidebar-nav .nav-item .nav-link {
  color: #000000 !important;
  background-color: transparent !important;
}

body.sidebar-light-theme .app .sidebar .nav .nav-item:hover .nav-link,
body.sidebar-light-theme .app .sidebar .navbar-nav .nav-item:hover .nav-link,
body.sidebar-light-theme .app .sidebar .sidebar-nav .nav-item:hover .nav-link,
body.sidebar-light-theme .app .sidebar .nav .nav-item .nav-link:hover,
body.sidebar-light-theme .app .sidebar .navbar-nav .nav-item .nav-link:hover,
body.sidebar-light-theme .app .sidebar .sidebar-nav .nav-item .nav-link:hover {
  background-color: #e8e8e8 !important;
  color: #000000 !important;
  transition: background-color 0.2s ease !important;
  border-color: transparent !important;
  border-left-color: transparent !important;
}

/* Make hover more visible */
body.sidebar-light-theme .app .sidebar .nav .nav-item:hover,
body.sidebar-light-theme .app .sidebar .navbar-nav .nav-item:hover,
body.sidebar-light-theme .app .sidebar .sidebar-nav .nav-item:hover {
  background-color: #e8e8e8 !important;
  transition: background-color 0.2s ease !important;
}

/* Ultimate override for CoreUI hover states */
.sidebar-light-theme .sidebar .nav-item:hover,
.sidebar-light-theme .sidebar .nav-item:hover .nav-link,
.sidebar-light-theme .sidebar .nav-item .nav-link:hover,
.sidebar-light-theme .sidebar .sidebar-nav .nav-item:hover,
.sidebar-light-theme .sidebar .sidebar-nav .nav-item:hover .nav-link,
.sidebar-light-theme .sidebar .sidebar-nav .nav-item .nav-link:hover,
.sidebar-light-theme .sidebar .navbar-nav .nav-item:hover,
.sidebar-light-theme .sidebar .navbar-nav .nav-item:hover .nav-link,
.sidebar-light-theme .sidebar .navbar-nav .nav-item .nav-link:hover {
  background-color: #e8e8e8 !important;
  color: #000000 !important;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: none !important;
  border: none !important;
  box-shadow: none !important;
}

/* Force all icons to be black in light theme */
body.sidebar-light-theme .sidebar .nav-icon,
body.sidebar-light-theme .sidebar .fa,
body.sidebar-light-theme .sidebar i,
body.sidebar-light-theme .sidebar .nav-item .nav-icon,
body.sidebar-light-theme .sidebar .nav-item .fa,
body.sidebar-light-theme .sidebar .nav-item i,
body.sidebar-light-theme .sidebar .nav-link .nav-icon,
body.sidebar-light-theme .sidebar .nav-link .fa,
body.sidebar-light-theme .sidebar .nav-link i,
body.sidebar-light-theme .sidebar .sidebar-nav .nav-icon,
body.sidebar-light-theme .sidebar .sidebar-nav .fa,
body.sidebar-light-theme .sidebar .sidebar-nav i,
body.sidebar-light-theme .sidebar .navbar-nav .nav-icon,
body.sidebar-light-theme .sidebar .navbar-nav .fa,
body.sidebar-light-theme .sidebar .navbar-nav i,
body.sidebar-light-theme .sidebar [class*="fa-"],
body.sidebar-light-theme .sidebar .nav-item [class*="fa-"],
body.sidebar-light-theme .sidebar .nav-link [class*="fa-"],
body.sidebar-light-theme .sidebar .sidebar-nav [class*="fa-"],
body.sidebar-light-theme .sidebar .navbar-nav [class*="fa-"] {
  color: #000000 !important;
}

/* Specific FontAwesome icon classes */
.sidebar-light-theme .sidebar .fa-tachometer-alt,
.sidebar-light-theme .sidebar .fa-list,
.sidebar-light-theme .sidebar .fa-question,
.sidebar-light-theme .sidebar .fa-shield-alt,
.sidebar-light-theme .sidebar .fa-users,
.sidebar-light-theme .sidebar .fa-building,
.sidebar-light-theme .sidebar .fa-globe,
.sidebar-light-theme .sidebar .fa-user,
.sidebar-light-theme .sidebar .fa-file-alt,
.sidebar-light-theme .sidebar .fa-book {
  color: #000000 !important;
}

.main {
  flex: 1;
  margin-left: 200px;
  min-height: 100vh;
  background-color: #f0f3f5;
  transition: margin-left 0.2s;
}

/* Mobile responsive */
@media (max-width: 991.98px) {
  .sidebar {
    margin-left: -200px;
  }

  .main {
    margin-left: 0;
  }

  .sidebar-show .sidebar {
    margin-left: 0;
  }
}

/* ===== Controle de Dropdowns - Fechados por Padrão ===== */

/* Esconder os children dos nav-dropdown por padrão */
.sidebar .nav-dropdown .nav-dropdown-items {
  display: none;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
}

/* Mostrar os children quando o dropdown estiver aberto */
.sidebar .nav-dropdown.open .nav-dropdown-items,
.sidebar .nav-dropdown.collapsed-popover-open .nav-dropdown-items {
  display: block;
  max-height: 1000px;
}

/* Estilização do indicador de dropdown (seta) */
.sidebar .nav-dropdown > .nav-link::after {
  content: "\f105";
  font-family: "FontAwesome";
  position: absolute;
  right: 1rem;
  transition: transform 0.3s ease;
}

/* Rotacionar a seta quando o dropdown estiver aberto */
.sidebar .nav-dropdown.open > .nav-link::after,
.sidebar .nav-dropdown.collapsed-popover-open > .nav-link::after {
  transform: rotate(90deg);
}

/* Garantir que os children fiquem ocultos por padrão usando CSS do CoreUI */
.sidebar
  .sidebar-nav
  .nav-dropdown:not(.open):not(.collapsed-popover-open)
  > .nav-dropdown-items,
.sidebar
  .sidebar-nav
  .nav-dropdown:not(.open):not(.collapsed-popover-open)
  .nav-dropdown-items {
  display: none !important;
  max-height: 0 !important;
  overflow: hidden !important;
}

/* Exibir children apenas quando estiver com a classe 'open' */
.sidebar .sidebar-nav .nav-dropdown.open > .nav-dropdown-items,
.sidebar .sidebar-nav .nav-dropdown.open .nav-dropdown-items,
.sidebar
  .sidebar-nav
  .nav-dropdown.collapsed-popover-open
  > .nav-dropdown-items,
.sidebar .sidebar-nav .nav-dropdown.collapsed-popover-open .nav-dropdown-items {
  display: block !important;
  max-height: none !important;
  overflow: visible !important;
}

/* Estilo adicional para itens de submenu */
.sidebar .nav-dropdown-items {
  padding-left: 0;
  list-style: none;
}

.sidebar .nav-dropdown-items .nav-item {
  padding-left: 2rem;
}

.sidebar .nav-dropdown-items .nav-link {
  padding-left: 2.5rem;
}

/* Sidebar Search Section Styles */
.sidebar-search-section .search-input-group {
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 20px !important;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.05) !important;
  transition: all 0.3s ease;
  margin-bottom: 5px;
}

.sidebar-search-section .search-input {
  background-color: transparent !important;
  border: none !important;
  color: #fff !important;
  box-shadow: none !important;
  padding-left: 15px;
}

.sidebar-search-section .search-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.sidebar-search-section .search-btn {
  color: rgba(255, 255, 255, 0.6) !important;
  padding-right: 12px;
  text-decoration: none !important;
}

.sidebar-search-section .search-btn:hover {
  color: #fff !important;
}

/* Light Theme Overrides */
.sidebar-light-theme .sidebar-search-section .search-input-group {
  border-color: rgba(0, 0, 0, 0.15) !important;
  background-color: #f8f9fa !important;
}

.sidebar-light-theme .sidebar-search-section .search-input {
  color: #000 !important;
}

.sidebar-light-theme .sidebar-search-section .search-input::placeholder {
  color: rgba(0, 0, 0, 0.4);
}

.sidebar-light-theme .sidebar-search-section .search-btn {
  color: rgba(0, 0, 0, 0.5) !important;
}

.sidebar-light-theme .sidebar-search-section .search-btn:hover {
  color: #000 !important;
}
</style>
